import { surveyIds } from '@chiroup/core/constants/SurveyIds';
import {
  buildSubjectiveFromTemplateHtml,
  hasSubjectiveTemplate,
} from '@chiroup/core/functions/buildCustomSurveySoapLines';
import { collectRenderedSurveyIds } from '@chiroup/core/functions/buildFlexibleDisplay';
import {
  buildCompletedAllergySurvey,
  buildCompletedMedicationSurvey,
  buildCompletedSurvey,
} from '@chiroup/core/functions/completedSurveysForNotes';
import { createName } from '@chiroup/core/functions/createName';
import { PatientSurvey } from '@chiroup/core/types/PatientSurvey.type';
import { ComplaintStatuses } from '@chiroup/core/types/PatientVisit.type';
import { useEffect, useMemo, useRef } from 'react';
import useEvaluations from '../../../../../hooks/useEvaluations';
import useModalities from '../../../../../hooks/useModalities';
import useMuscles from '../../../../../hooks/useMuscles';
import useSupplements from '../../../../../hooks/useSupplements';
import useTests from '../../../../../hooks/useTests';
import useTreatments from '../../../../../hooks/useTreatments';
import { CustomizationListItem } from '../../../../clinical-skills/CustomizationListItem.type';
import useComplaintConditions from '../../../../patients/hooks/useComplaintConditions';
import { getWYSIWYGAssessmentContentFn } from '../../visits/helpers/assessmentStrBuilder';
import { getWYSIWYGObjectiveContentFn } from '../../visits/helpers/objectiveStrBuilder';
import { getWYSIWYGPlanContent } from '../../visits/helpers/planStrBuilder';
import { getWYSIWYGSubjectiveContentFnV2 } from '../../visits/helpers/subjectiveStrBuilder';
import { getUnifiedObjectiveContentFn } from '../../visits/helpers/unifiedObjectiveStrBuilder';
import { useCollaborativeEncounterContext } from '../contexts/collaborativeEncounter.context';
import { overlayApiAuthoritativeEncounterInfo } from './apiAuthoritativeEncounterInfo';

const stripTags = {
  br: (str: string) => str.replace(/<\/br>|<br\s*\/>/g, ''),
  p: (str: string) => str.replace(/<p><\/p>|<\/p>\n?/g, ''),
};

/**
 * Hook that automatically regenerates SOAP note sections when underlying data changes.
 * Only regenerates if:
 * - enabled is true
 * - The note isn't signed
 * - The section hasn't been manually edited (soapCustomEditWarning)
 */
export function useSoapAutoRegeneration(enabled: boolean = true) {
  const { visitForm, updateField, getField, hasLocalData, isSynced } =
    useCollaborativeEncounterContext();

  // Extract condition IDs from complaints for useComplaintConditions
  const conditionIds = useMemo(() => {
    return (
      visitForm?.encounterInfo?.complaints?.flatMap(
        (complaint: any) =>
          complaint?.assessment?.conditions?.map((item: any) => item.conditionId) || []
      ) || []
    );
  }, [visitForm?.encounterInfo?.complaints]);

  // Fetch all data needed for SOAP generation (matching V1)
  const { data: evals } = useEvaluations({ refetchOnMount: false });
  const { data: muscles } = useMuscles({ refetchOnMount: false });
  const { data: testsData } = useTests({
    refetchOnMount: false,
    ids: Object.keys(visitForm?.plan?.imagingAndTests || {}).map(item => parseInt(item)),
  });
  const { data: modalitiesData } = useModalities({
    refetchOnMount: false,
    ids: Object.keys(visitForm?.plan?.modalities || {}).map(item => parseInt(item, 10)),
  });
  const { data: supplementsData } = useSupplements({
    refetchOnMount: false,
    ids: Object.keys(visitForm?.plan?.supplementsAndSupplies || {}).map(item => parseInt(item)),
  });
  const { data: treatmentsData } = useTreatments({
    type: 'Clinical',
    hide: false,
    refetchOnMount: false,
  });
  const { allConditionsRetrieved } = useComplaintConditions(conditionIds, true);

  // Track previous values to detect changes
  // Each effect has its own initialization flag to handle async data loading correctly
  const prevPlanRef = useRef<string | undefined>(undefined);
  const prevPlanWizardCategoryRef = useRef<any>(undefined);
  const isPlanInitializedRef = useRef(false);
  // Track if plan had content when we first initialized - if so, never auto-regenerate
  const planHadInitialContentRef = useRef(false);

  // Store serialized strings instead of array references to detect Yjs mutations
  const prevComplaintsForAssessmentRef = useRef<string | undefined>(undefined);
  const isAssessmentInitializedRef = useRef(false);
  // Track if assessment had content when we first initialized
  const assessmentHadInitialContentRef = useRef(false);

  const prevComplaintsSerializedRef = useRef<string | undefined>(undefined);
  const prevComplaintSummariesRef = useRef<string | undefined>(undefined);
  const prevSurveysToDocumentRef = useRef<string | undefined>(undefined);
  const prevSummarySurveyIdsRef = useRef<string | undefined>(undefined);
  const prevReferencedSurveysRef = useRef<string | undefined>(undefined);
  const isSubjectiveInitializedRef = useRef(false);
  // Track if subjective had content when we first initialized
  const subjectiveHadInitialContentRef = useRef(false);

  const prevUnifiedObjectiveRef = useRef<string | undefined>(undefined);
  const isObjectiveInitializedRef = useRef(false);
  // Track if objective had content when we first initialized
  const objectiveHadInitialContentRef = useRef(false);
  const prevObjectiveComplaintCountRef = useRef<number>(0);

  // Regenerate plan when plan or planWizardCategory changes
  useEffect(() => {
    if (!enabled || !visitForm || !hasLocalData || visitForm?.signed) return;
    // Check the warning in live Yjs, not just render-time visitForm — this
    // effect can run right after a debounced flush that set the warning, and
    // regenerating then would overwrite the manual edit the warning flags.
    // Same reason the writes below merge over live notes.
    if ((getField('soapCustomEditWarning') ?? visitForm?.soapCustomEditWarning)?.plan) return;

    const currentPlan = JSON.stringify(visitForm?.plan);
    const currentPlanWizardCategory = JSON.stringify(visitForm?.planWizardCategory);

    // Wait for isSynced before initializing to ensure we see full server data
    if (!isPlanInitializedRef.current) {
      if (!isSynced) return;

      prevPlanRef.current = currentPlan;
      prevPlanWizardCategoryRef.current = currentPlanWizardCategory;
      isPlanInitializedRef.current = true;
      const planContent = visitForm?.notes?.plan;
      planHadInitialContentRef.current = !!(
        planContent &&
        typeof planContent === 'string' &&
        planContent.trim()
      );

      return;
    }

    // Never auto-regenerate if plan had content when we initialized
    // This preserves user's saved content across page refreshes
    if (planHadInitialContentRef.current) {
      prevPlanRef.current = currentPlan;
      prevPlanWizardCategoryRef.current = currentPlanWizardCategory;

      return;
    }

    const planChanged =
      prevPlanRef.current !== currentPlan ||
      prevPlanWizardCategoryRef.current !== currentPlanWizardCategory;

    if (planChanged && visitForm?.planWizardCategory) {
      const generatedPlan = getWYSIWYGPlanContent({
        visitForm,
        modalitiesData: modalitiesData || { data: [] },
        testsData: testsData || { data: [] },
        supplementsData: supplementsData || { data: [], itemTaxHash: {} },
        treatmentsData: treatmentsData || { data: [] },
        muscles: muscles || { data: [] },
      });

      const cleanedPlan = stripTags.br(generatedPlan);
      const liveNotes = getField('notes') ?? visitForm?.notes;

      if (cleanedPlan !== (liveNotes?.plan || '')) {
        updateField('notes', { ...liveNotes, plan: cleanedPlan });
      }
    }

    prevPlanRef.current = currentPlan;
    prevPlanWizardCategoryRef.current = currentPlanWizardCategory;
  }, [
    enabled,
    hasLocalData,
    isSynced,
    visitForm?.signed,
    visitForm?.plan,
    visitForm?.planWizardCategory,
    visitForm?.notes?.plan,
    visitForm?.soapCustomEditWarning?.plan,
    updateField,
    getField,
    visitForm,
    modalitiesData,
    testsData,
    supplementsData,
    treatmentsData,
    muscles,
  ]);

  // Regenerate assessment when complaints' assessment data changes
  useEffect(() => {
    if (!enabled || !visitForm || !hasLocalData || visitForm?.signed) return;
    // Live Yjs first — see the plan effect's comment
    if ((getField('soapCustomEditWarning') ?? visitForm?.soapCustomEditWarning)?.assessment) {
      return;
    }

    const currentComplaints = visitForm?.encounterInfo?.complaints;

    // Serialize complaints for comparison (handles Yjs mutations)
    const currentComplaintsForAssessment = JSON.stringify(
      currentComplaints?.map((c: any) => ({
        id: c.id,
        status: c.status || null,
        prn: c.prn || c.assessment?.prn || false,
        assessment: c.assessment || null,
      })) || []
    );

    if (!isAssessmentInitializedRef.current) {
      if (!isSynced) return;

      prevComplaintsForAssessmentRef.current = currentComplaintsForAssessment;
      isAssessmentInitializedRef.current = true;
      const assessmentContent = visitForm?.notes?.assessment;
      assessmentHadInitialContentRef.current = !!(
        assessmentContent &&
        typeof assessmentContent === 'string' &&
        assessmentContent.trim()
      );

      return;
    }

    // Never auto-regenerate if assessment had content when we initialized
    // UNLESS a complaint was removed (deletion must regenerate SOAP text).
    const prevAssessmentComplaintCount = prevComplaintsForAssessmentRef.current
      ? (JSON.parse(prevComplaintsForAssessmentRef.current) as unknown[]).length
      : 0;
    const currentAssessmentComplaintCount = currentComplaints?.length ?? 0;
    const assessmentComplaintsWereRemoved =
      currentAssessmentComplaintCount < prevAssessmentComplaintCount;

    if (assessmentHadInitialContentRef.current && !assessmentComplaintsWereRemoved) {
      prevComplaintsForAssessmentRef.current = currentComplaintsForAssessment;

      return;
    }

    if (assessmentComplaintsWereRemoved) {
      assessmentHadInitialContentRef.current = false;
    }

    const complaintsChangedForAssessment =
      prevComplaintsForAssessmentRef.current !== currentComplaintsForAssessment;

    if (complaintsChangedForAssessment && currentComplaints?.length) {
      // Get complaints to document — exclude skipped/paused but include inactive (builder handles deactivation message)
      const complaintsToDocument = currentComplaints.filter(
        (c: any) => !c.skipped && c.status !== ComplaintStatuses.PAUSED
      );

      if (complaintsToDocument.length > 0) {
        const generatedAssessment = getWYSIWYGAssessmentContentFn({
          complaints: complaintsToDocument,
          totalComplaints: complaintsToDocument.length,
          allConditionsRetrieved: allConditionsRetrieved || {},
          currentVisit: visitForm?.visitNumber || 1,
          totalVisits: visitForm?.encounterInfo?.totalVisits || 0,
          encounterInfo: visitForm?.encounterInfo,
          reevaluation: visitForm?.visitNumber !== 1,
        })();

        const cleanedAssessment = stripTags.br(generatedAssessment);
        const liveNotes = getField('notes') ?? visitForm?.notes;

        if (cleanedAssessment !== (liveNotes?.assessment || '')) {
          updateField('notes', { ...liveNotes, assessment: cleanedAssessment });
        }
      }
    }

    // Store the serialized string, not the reference (to detect Yjs mutations)
    prevComplaintsForAssessmentRef.current = currentComplaintsForAssessment;
  }, [
    enabled,
    hasLocalData,
    isSynced,
    visitForm,
    visitForm?.signed,
    visitForm?.encounterInfo?.complaints,
    visitForm?.notes?.assessment,
    visitForm?.soapCustomEditWarning?.assessment,
    getField,
    visitForm?.visitNumber,
    visitForm?.encounterInfo?.totalVisits,
    visitForm?.encounterInfo,
    updateField,
    visitForm?.notes,
    allConditionsRetrieved,
  ]);

  // Create a serialized key for complaints subjective field
  // This ensures the effect runs when subjective notes, bodypicker, or complaint name change
  // even if Yjs mutates array in place
  const complaintsSubjectiveKey = JSON.stringify(
    visitForm?.encounterInfo?.complaints?.map((c: any) => ({
      id: c.id,
      status: c.status || null,
      subjective: c.subjective || null,
      bodypicker: c.bodypicker || null,
      complaint: c.complaint || null, // Complaint name (derived from bodypicker)
    })) || []
  );

  // Regenerate subjective when complaints' subjective data or complaintSummaries changes
  useEffect(() => {
    if (!enabled || !visitForm || !hasLocalData || visitForm?.signed) return;
    // Live Yjs first — see the plan effect's comment
    if ((getField('soapCustomEditWarning') ?? visitForm?.soapCustomEditWarning)?.subjective) {
      return;
    }

    const currentComplaints = visitForm?.encounterInfo?.complaints;
    const currentComplaintSummaries = JSON.stringify(visitForm?.complaintSummaries || {});

    // Serialize complaints for comparison (handles Yjs mutations)
    // Extract only the fields we care about for subjective regeneration
    // Include bodypicker and complaint name since they affect complaintNames in SOAP generation
    const currentComplaintsSerialized = JSON.stringify(
      currentComplaints?.map((c: any) => ({
        id: c.id,
        status: c.status || null,
        subjective: c.subjective || null,
        assessment: c.assessment || null,
        bodypicker: c.bodypicker || null,
        complaint: c.complaint || null, // Complaint name (derived from bodypicker)
      })) || []
    );

    const currentSurveysToDocument = JSON.stringify(
      visitForm?.encounterInfo?.surveysToDocument || {}
    );

    // complaintId → patientSurveyId, sorted for stable comparison. A changed
    // id means a survey was newly completed (clear + redo mints a new one).
    const currentSummarySurveyIds = JSON.stringify(
      Object.entries(visitForm?.complaintSummaries || {})
        .map(([id, s]) => [id, (s as any)?.patientSurveyId ?? null])
        .sort((a, b) => String(a[0]).localeCompare(String(b[0])))
    );

    // Template identity of summary-referenced surveys. The CCS template branch
    // resolves behavesAs/subjectiveTemplate from visitForm.surveys when the
    // summary wasn't stamped, so a referenced survey arriving after the
    // summaries must trigger a regen even though the summaries didn't change.
    const currentReferencedSurveys = JSON.stringify(
      Object.values(visitForm?.complaintSummaries || {})
        .map(s => String((s as any)?.patientSurveyId || ''))
        .filter(Boolean)
        .sort()
        .map(id => {
          const survey = (visitForm?.surveys || {})[id] as any;

          // Survey presence is part of the identity: index-placeholder
          // resolution needs the survey's questions even when the summary
          // already carries a stamped template, so hydration alone must re-run.
          return [
            id,
            !!survey,
            survey?.survey?.behavesAs ?? null,
            survey?.survey?.subjectiveTemplate ?? null,
          ];
        })
    );

    if (!isSubjectiveInitializedRef.current) {
      if (!isSynced) return;

      prevComplaintsSerializedRef.current = currentComplaintsSerialized;
      prevComplaintSummariesRef.current = currentComplaintSummaries;
      prevSurveysToDocumentRef.current = currentSurveysToDocument;
      prevSummarySurveyIdsRef.current = currentSummarySurveyIds;
      prevReferencedSurveysRef.current = currentReferencedSurveys;
      isSubjectiveInitializedRef.current = true;
      const subjectiveContent = visitForm?.notes?.subjective;
      subjectiveHadInitialContentRef.current = !!(
        subjectiveContent &&
        typeof subjectiveContent === 'string' &&
        subjectiveContent.trim()
      );

      return;
    }

    // If surveys are flagged to document but allSurveys is still undefined,
    // regenerating now writes a subjective missing their answers, then a second
    // regen pops them in (the flicker). Bail without updating the refs so this
    // effect re-runs once allSurveys arrives. A defined-but-empty allSurveys
    // proceeds, so a flagged-but-absent survey can't block forever.
    const hasSurveysFlaggedToDocument = Object.values(
      visitForm?.encounterInfo?.surveysToDocument || {}
    ).some(Boolean);
    if (hasSurveysFlaggedToDocument && !visitForm?.allSurveys) {
      return;
    }

    // Never auto-regenerate if subjective had content when we initialized
    // UNLESS surveysToDocument changed (e.g., API data arrived with SOAP
    // preferences after Yjs already generated subjective without surveys)
    // OR a complaint was removed (deletion must regenerate the SOAP text so
    // "Complaint X of Y" headers reflect the new count)
    // OR a summary's patientSurveyId changed (a survey was newly completed,
    // e.g. clear + redo — real new answers, not identity churn; hand edits
    // are still protected by the warning check above)
    // OR a referenced survey's template identity changed (a late-arriving
    // survey must upgrade a Q&A-fallback subjective to its template).
    const surveysToDocumentChanged = prevSurveysToDocumentRef.current !== currentSurveysToDocument;
    const completedSurveyChanged = prevSummarySurveyIdsRef.current !== currentSummarySurveyIds;
    const referencedSurveysChanged = prevReferencedSurveysRef.current !== currentReferencedSurveys;

    // Detect complaint removal by comparing array lengths
    const prevComplaintCount = prevComplaintsSerializedRef.current
      ? (JSON.parse(prevComplaintsSerializedRef.current) as unknown[]).length
      : 0;
    const currentComplaintCount = currentComplaints?.length ?? 0;
    const complaintsWereRemoved = currentComplaintCount < prevComplaintCount;

    if (
      subjectiveHadInitialContentRef.current &&
      !surveysToDocumentChanged &&
      !complaintsWereRemoved &&
      !completedSurveyChanged &&
      !referencedSurveysChanged
    ) {
      prevComplaintsSerializedRef.current = currentComplaintsSerialized;
      prevComplaintSummariesRef.current = currentComplaintSummaries;

      return;
    }

    // Once we bypass the guard due to complaint removal, clear it so
    // subsequent changes also regenerate properly.
    if (complaintsWereRemoved) {
      subjectiveHadInitialContentRef.current = false;
    }

    // Compare against the stored serialized string (not a reference)
    const complaintsChangedForSubjective =
      prevComplaintsSerializedRef.current !== currentComplaintsSerialized;

    const complaintSummariesChanged =
      prevComplaintSummariesRef.current !== currentComplaintSummaries;

    // Regenerate if complaints, complaintSummaries, surveysToDocument, or the
    // summary-referenced surveys changed
    if (
      (complaintsChangedForSubjective ||
        complaintSummariesChanged ||
        surveysToDocumentChanged ||
        referencedSurveysChanged) &&
      currentComplaints?.length
    ) {
      // Get complaints to document — exclude skipped/paused but include inactive (builder handles deactivation)
      const complaintsToDocument = currentComplaints.filter(
        (c: any) => !c.skipped && c.status !== ComplaintStatuses.PAUSED && !c.prn
      );

      if (complaintsToDocument.length > 0) {
        // Build activeComplaintsProblemAreas and complaintNames for subjective generation
        const activeComplaintsProblemAreas: { [key: string]: boolean } = {};
        const complaintNames: { [key: string]: { name: string; order: number } } = {};

        complaintsToDocument.forEach((complaint: any, index: number) => {
          if (complaint?.id) {
            activeComplaintsProblemAreas[complaint.id] = true;
            complaintNames[complaint.id] = {
              name: complaint?.bodypicker?.parts
                ? createName(complaint.bodypicker.parts)
                : complaint?.complaint || '',
              order: index,
            };
          }
        });

        let generatedSubjective = getWYSIWYGSubjectiveContentFnV2({
          complaints: complaintsToDocument,
          totalComplaints: complaintsToDocument.length,
          complaintSummaries: visitForm?.complaintSummaries,
          activeComplaintsProblemAreas,
          complaintNames,
          surveys: visitForm?.surveys,
        })();

        const renderedSurveyIds = collectRenderedSurveyIds(
          visitForm?.complaintSummaries,
          activeComplaintsProblemAreas
        );

        // Add completed surveys to subjective. Toggle map via the overlay:
        // a non-empty live Yjs map is user toggles (visitForm never reflects
        // them) and must win over the API-computed map.
        const { surveysToDocument } = overlayApiAuthoritativeEncounterInfo(
          (getField('encounterInfo') || {}) as Record<string, any>,
          visitForm?.encounterInfo || {}
        );

        const allSurveysToDocument = visitForm?.allSurveys?.reduce(
          (arr: PatientSurvey[], survey: PatientSurvey) => {
            if (
              surveysToDocument &&
              surveysToDocument[survey?.id] &&
              !renderedSurveyIds.has(survey?.id)
            ) {
              arr.push(survey);
            }

            return arr;
          },
          []
        );

        if (allSurveysToDocument?.length) {
          allSurveysToDocument.forEach((survey: PatientSurvey) => {
            if (survey?.surveyId === surveyIds.medicationHistory) {
              generatedSubjective += buildCompletedMedicationSurvey(survey);
            } else if (survey.surveyId === surveyIds.allergy) {
              generatedSubjective += buildCompletedAllergySurvey(survey);
            } else if (hasSubjectiveTemplate(survey)) {
              generatedSubjective += buildSubjectiveFromTemplateHtml(survey);
            } else {
              generatedSubjective += buildCompletedSurvey(survey);
            }
          });
        }

        const cleanedSubjective = stripTags.p(stripTags.br(generatedSubjective));
        const liveNotes = getField('notes') ?? visitForm?.notes;

        if (cleanedSubjective !== (liveNotes?.subjective || '')) {
          updateField('notes', { ...liveNotes, subjective: cleanedSubjective });
        }
      }
    }

    // Store the serialized string, not the reference (to detect Yjs mutations)
    prevComplaintsSerializedRef.current = currentComplaintsSerialized;
    prevComplaintSummariesRef.current = currentComplaintSummaries;
    prevSurveysToDocumentRef.current = currentSurveysToDocument;
    prevSummarySurveyIdsRef.current = currentSummarySurveyIds;
    prevReferencedSurveysRef.current = currentReferencedSurveys;
  }, [
    enabled,
    hasLocalData,
    isSynced,
    visitForm,
    visitForm?.signed,
    // Use serialized complaints key to detect changes even when Yjs mutates array in place
    complaintsSubjectiveKey,
    visitForm?.notes?.subjective,
    visitForm?.soapCustomEditWarning?.subjective,
    getField,
    visitForm?.complaintSummaries,
    visitForm?.allSurveys,
    visitForm?.surveys,
    visitForm?.encounterInfo?.surveysToDocument,
    updateField,
    visitForm?.notes,
  ]);

  // Regenerate objective when unifiedObjective changes
  useEffect(() => {
    if (!enabled || !visitForm || !hasLocalData || visitForm?.signed) return;
    // Live Yjs first — see the plan effect's comment
    if ((getField('soapCustomEditWarning') ?? visitForm?.soapCustomEditWarning)?.objective) {
      return;
    }

    const currentUnifiedObjective = JSON.stringify(visitForm?.unifiedObjective);

    const currentObjectiveComplaintCount = visitForm?.encounterInfo?.complaints?.length ?? 0;

    if (!isObjectiveInitializedRef.current) {
      if (!isSynced) return;

      prevUnifiedObjectiveRef.current = currentUnifiedObjective;
      prevObjectiveComplaintCountRef.current = currentObjectiveComplaintCount;
      isObjectiveInitializedRef.current = true;
      const objectiveContent = visitForm?.notes?.objective;
      objectiveHadInitialContentRef.current = !!(
        objectiveContent &&
        typeof objectiveContent === 'string' &&
        objectiveContent.trim()
      );

      return;
    }

    // Never auto-regenerate if objective had content when we initialized
    // UNLESS a complaint was removed (deletion must regenerate SOAP text).
    const objectiveComplaintsWereRemoved =
      currentObjectiveComplaintCount < prevObjectiveComplaintCountRef.current;

    if (objectiveHadInitialContentRef.current && !objectiveComplaintsWereRemoved) {
      prevUnifiedObjectiveRef.current = currentUnifiedObjective;
      prevObjectiveComplaintCountRef.current = currentObjectiveComplaintCount;

      return;
    }

    if (objectiveComplaintsWereRemoved) {
      objectiveHadInitialContentRef.current = false;
    }

    const unifiedObjectiveChanged = prevUnifiedObjectiveRef.current !== currentUnifiedObjective;

    if (unifiedObjectiveChanged && visitForm?.unifiedObjective) {
      const isV2Visit = visitForm.version === 2;
      let generatedObjective = '';

      if (isV2Visit) {
        generatedObjective =
          getUnifiedObjectiveContentFn(
            visitForm,
            muscles || { data: [] },
            (evals || { data: [] }) as unknown as { data: CustomizationListItem[] }
          ) || '';
      } else {
        // Fallback to V1 objective generation if needed - pass real muscles and evals data
        const complaintsToDocument = visitForm?.encounterInfo?.complaints?.filter(
          (c: any) => !c.skipped && c.status !== ComplaintStatuses.PAUSED && !c.prn
        );
        generatedObjective =
          getWYSIWYGObjectiveContentFn(
            visitForm,
            {}, // previouslyActiveComplaintsProblemAreas
            muscles || { data: [] },
            (evals || { data: [] }) as unknown as { data: CustomizationListItem[] },
            complaintsToDocument || []
          ) || '';
      }

      const cleanedObjective = stripTags.br(generatedObjective);
      const liveNotes = getField('notes') ?? visitForm?.notes;

      if (cleanedObjective !== (liveNotes?.objective || '')) {
        updateField('notes', { ...liveNotes, objective: cleanedObjective });
      }
    }

    prevUnifiedObjectiveRef.current = currentUnifiedObjective;
    prevObjectiveComplaintCountRef.current = currentObjectiveComplaintCount;
  }, [
    enabled,
    hasLocalData,
    isSynced,
    visitForm,
    visitForm?.signed,
    visitForm?.unifiedObjective,
    visitForm?.notes?.objective,
    visitForm?.soapCustomEditWarning?.objective,
    getField,
    visitForm?.version,
    visitForm?.encounterInfo?.complaints,
    updateField,
    evals,
    muscles,
  ]);
}
